import { useCallback } from 'react';
import {
  useProjectState,
  useProjectTemplates,
  useCreateProject,
  useFindFreeProjectName,
  usePublicTemplateProjects,
} from '@client/store/project';
import { IProjectInfo, ProjectID } from '@shared/models/project.model';
import {
  EditProjectFormData,
  useEditProjectDialogStore,
  ProjectType,
  DialogType,
} from './edit-project.store';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import {
  CreateProjectPayload,
  EditableProjectData,
  UpdateProjectDetailsPayload,
} from '@shared/models/project.model';
import { ISnackbarOptions } from '@client/hooks/use-snackbar.hooks';
import amplitudeLog from '@client/amplitude';
import {
  AMP_PROJECT_CREATE_FROM_BLANK,
  AMP_PROJECT_CREATE_FROM_FILE,
  AMP_PROJECT_CREATE_FROM_TEMPLATE,
  AMP_PROJECT_DUPLICATE,
  AMP_PROJECT_EDIT_PROJECT,
  AMP_PROJECT_EDIT_TEMPLATE,
  AMP_PROJECT_CREATE_TEMPLATE,
} from '@shared/constants';
import { omitUndefined } from '@shared/helpers/object.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { useSelectedOrganizationName } from '@client/store/organization';

export const useSubmitProjectData = (mode: DialogType, type: ProjectType) => {
  const { folderId, id, project } = useEditProjectDialogStore();
  const { updateProjectDetails } = useProjectState('updateProjectDetails');
  const organization = useSelectedOrganizationName();
  const createProject = useCreateProject(organization);
  const logEvent = useLogEditProjectDialog();

  return useCallback(
    async (
      { name, description, completed_at }: EditProjectFormData,
      overwriteId?: ProjectID,
    ) => {
      if (createProject === undefined) {
        throw new Error(
          'Create project function is not available. No organization is selected. This should never happen.',
        );
      }
      if (type !== 'template' && overwriteId) {
        throw new Error(
          'Forbidden to overwrite template with non template project',
        );
      }

      const snackbarOptions: ISnackbarOptions = {
        successMessage: `Project ${type === 'template' ? 'template' : ''} created`,
        errorMessage: `Failed to create project ${type === 'template' ? 'template' : ''}`,
      };

      const data: EditableProjectData = {
        name,
        description,
        completed_at: completed_at?.toString() ?? null,
      };

      // create project from file upload
      if (project) {
        return createProject({
          from: 'file',
          data: { ...project, ...data },
        });
      }

      // no project or reference provided, create an empty project
      if (id === undefined) {
        return createProject({
          from: 'blank',
          data: { ...data, parent_id: folderId },
        });
      }

      // edit or create project by reference
      switch (mode) {
        case 'edit': {
          const payload: UpdateProjectDetailsPayload = {
            reference: id,
            data,
            overwriteId,
          };
          // event logging is handled in createProject hook for other events besides edit
          logEvent(payload);
          return updateProjectDetails(payload);
        }
        case 'duplicate':
        case 'create': {
          const payload: CreateProjectPayload = {
            from: 'project_ref',
            reference: id,
            overwriteId,
            data: {
              ...data,
              template: type === 'template',
              parent_id: folderId,
            },
          };
          return createProject(payload, snackbarOptions);
        }
        default: {
          return assertUnreachable(mode);
        }
      }
    },
    [
      type,
      project,
      id,
      mode,
      createProject,
      folderId,
      logEvent,
      updateProjectDetails,
    ],
  );
};

/**
 * Log the edit project dialog event to amplitude
 */
export const useLogEditProjectDialog = () => {
  const { mode, type } = useEditProjectDialogStore();

  return useCallback(
    (payload: CreateProjectPayload | UpdateProjectDetailsPayload) => {
      const source = 'from' in payload ? payload.from : '';
      const data =
        source === 'file' ? getFilePayloadData(payload) : payload.data;

      if ('overwriteId' in payload && payload.overwriteId) {
        amplitudeLog('Replace Template', {
          name: data?.name,
          overwriteId: payload.overwriteId,
        });
      }
      if (type && mode) {
        const event = resolveProjectDialogEvent(mode, type, source);
        if (event) {
          amplitudeLog(event, data ? { ...omitUndefined(data) } : {});
        }
      }
    },
    [mode, type],
  );
};

/**
 * Resolve the Amplitude event name for a project dialog action based on
 * {@link DialogType mode}, {@link ProjectType type} and creation source.
 *
 * @returns The matching AMP constant, or `undefined` if no event applies.
 */
const resolveProjectDialogEvent = (
  mode: DialogType,
  type: ProjectType,
  source: string,
): string | undefined => {
  switch (mode) {
    case 'create':
      if (type === 'template') return AMP_PROJECT_CREATE_TEMPLATE;
      switch (source) {
        case 'blank':
          return AMP_PROJECT_CREATE_FROM_BLANK;
        case 'file':
          return AMP_PROJECT_CREATE_FROM_FILE;
        case 'project_ref':
          return AMP_PROJECT_CREATE_FROM_TEMPLATE;
      }
      break;
    case 'duplicate':
      return AMP_PROJECT_DUPLICATE;
    case 'edit':
      return type === 'template'
        ? AMP_PROJECT_EDIT_TEMPLATE
        : AMP_PROJECT_EDIT_PROJECT;
  }
};

const getFilePayloadData = (
  payload: CreateProjectPayload | UpdateProjectDetailsPayload,
): EditableProjectData | undefined => {
  if ('from' in payload && payload.from === 'file') {
    return {
      name: payload.data?.name,
      description: payload.data?.description,
      completed_at: payload.data?.completed_at,
    };
  }
};

export const useConfirmReplaceTemplate = () => {
  const confirm = useIsConfirmed();

  return useCallback(
    async (name: string) => {
      return confirm({
        title: `A template named "${name}" already exists.`,
        content: 'Do you want to replace the existing template?',
        confirmationText: 'Replace',

        cancellationButtonProps: {
          variant: 'contained',
          ref: (ref) => ref?.focus(),
        },
        confirmationButtonProps: { variant: 'text' },
      });
    },
    [confirm],
  );
};

export const useFindDuplicateNameTemplateProjectId = () => {
  const templates = useProjectTemplates();

  return useCallback(
    (name: string, id: IProjectInfo['id']) => {
      const templateWithEqualName = templates.find(
        (project) => id !== project.id && project.name === name,
      );
      return templateWithEqualName
        ? Number(templateWithEqualName.id)
        : undefined;
    },
    [templates],
  );
};

export const useNewProjectName = (
  type: ProjectType,
  mode: DialogType,
  name = 'New project',
) => {
  const findFreeProjectName = useFindFreeProjectName();
  const templates = useProjectTemplates();
  const publicTemplates = usePublicTemplateProjects();

  if (mode === 'duplicate') {
    return findFreeProjectName(name);
  }
  if (mode === 'create' && type === 'project') {
    const templateWithEqualName = [...templates, ...publicTemplates].find(
      (p) => p.name === name,
    );
    return findFreeProjectName(templateWithEqualName ? `New ${name}` : name);
  }
  return name;
};
